import { usePageRefreshKey } from '../../../../context/PageRefreshContext';
import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Icon } from '@iconify/react';
import InvoiceDetailModal from './components/InvoiceDetailModal';
import { getInvoices, getInvoiceStatistics, getInvoiceDetails, deleteInvoice, voidInvoice, postInvoice, bulkPostInvoices, bulkDeleteInvoices } from '../../../../redux/actions/SocietyPortal/InvoiceAction/InvoiceAction';
import { getInvoiceTypes } from '../../../../redux/actions/SocietyPortal/InvoiceTypeAction/InvoiceTypeAction';
import { getWingsDropdown, getFlatsDropdown } from '../../../../redux/actions/WingsAndFlatsActions/WingsAndFlatsAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { PaginationLimit } from '../../../../components/PaginationLimit';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_INVOICES_CREATE, SOCIETY_PORTAL_INVOICES_BULK_PRINT, SOCIETY_PORTAL_INVOICES_BULK_CREATE } from '../../../../Routing/routes';
import { formatIstDate } from '../../../../utils/helpers/istDate';

const STATUSES = [
    { value: 'DRAFT', label: 'Draft' },
    { value: 'POSTED', label: 'Posted' },
    { value: 'PAID', label: 'Paid' },
    { value: 'PARTIAL', label: 'Partial' },
    { value: 'VOID', label: 'Void' },
    { value: 'CANCELLED', label: 'Cancelled' }
];

const Invoices = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const societyId = selectedSocietyStorer.getSocietyId();

    // Redux selectors
    const {
        invoicesData,
        invoicesPagination,
        loading,
        statistics,
        statisticsLoading,
        invoiceDetails,
        detailsLoading,
        deleteLoading,
        voidLoading,
        postLoading,
        bulkPostLoading,
        bulkDeleteLoading
    } = useSelector(state => state.InvoiceReducer);
    const { invoiceTypesData } = useSelector(state => state.InvoiceTypeReducers);
    const { wingsDropdown, flatsDropdown } = useSelector(state => state.WingsAndFlatsReducers);

    // Local state
    const [searchQuery, setSearchQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    // ?invoice_type_id= pre-selects the billing type (Billing Types -> View Bills).
    const location = useLocation();
    const [filterInvoiceType, setFilterInvoiceType] = useState(
        () => new URLSearchParams(location.search).get('invoice_type_id') || 'all'
    );
    const [filterWing, setFilterWing] = useState('all');
    const [filterFlat, setFilterFlat] = useState('all');
    const [filterCarriedForward, setFilterCarriedForward] = useState('all');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    const [viewMode, setViewMode] = useState('list');
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [searchParams, setSearchParams] = useState({
        page: 1,
        limit: 10,
        society_id: societyId
    });

    // Fetch dropdown data on mount
    useEffect(() => {
        dispatch(getInvoiceTypes({ society_id: societyId, page: 1, limit: 100 }));
        dispatch(getWingsDropdown({ society_id: societyId }));
    }, [dispatch, societyId]);

    // Fetch flats when wing filter changes
    useEffect(() => {
        if (filterWing && filterWing !== 'all') {
            dispatch(getFlatsDropdown({ wing_id: filterWing }));
        }
        setFilterFlat('all');
    }, [dispatch, filterWing]);

    // Fetch invoices on mount and when search params change
    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        const params = {
            ...searchParams,
            search: searchQuery || undefined,
            status: filterStatus !== 'all' ? filterStatus : undefined,
            invoice_type_id: filterInvoiceType !== 'all' ? Number(filterInvoiceType) : undefined,
            wing_id: filterWing !== 'all' ? Number(filterWing) : undefined,
            flat_id: filterFlat !== 'all' ? Number(filterFlat) : undefined,
            is_carried_forward: filterCarriedForward !== 'all' ? Number(filterCarriedForward) : undefined,
            from_date: fromDate || undefined,
            to_date: toDate || undefined,
        };
        dispatch(getInvoices(params));
    }, [dispatch, searchParams, searchQuery, filterStatus, filterInvoiceType, filterWing, filterFlat, filterCarriedForward, fromDate, toDate, refreshKey]);

    // Fetch statistics on mount
    useEffect(() => {
        dispatch(getInvoiceStatistics({ society_id: societyId }));
    }, [dispatch, societyId, refreshKey]);

    // Compute stats from API response
    const stats = useMemo(() => {
        if (!statistics || !Array.isArray(statistics)) {
            return {
                total: invoicesData?.length || 0,
                paid: 0,
                pending: 0,
                overdue: 0,
                collectedAmount: 0,
                pendingAmount: 0
            };
        }

        let total = 0, paid = 0, pending = 0, overdue = 0, collectedAmount = 0, pendingAmount = 0;

        statistics.forEach(stat => {
            const count = parseInt(stat.count) || 0;
            const totalAmt = parseFloat(stat.total_amount) || 0;
            const totalDue = parseFloat(stat.total_due) || 0;

            total += count;
            collectedAmount += (totalAmt - totalDue);
            pendingAmount += totalDue;

            if (stat.status === 'PAID') paid = count;
            if (stat.status === 'POSTED' || stat.status === 'DRAFT') pending += count;
            if (stat.status === 'PARTIAL') overdue += count; // Using PARTIAL as overdue indicator
        });

        return { total, paid, pending, overdue, collectedAmount, pendingAmount };
    }, [statistics, invoicesData]);

    const getStatusColor = (status) => {
        switch (status?.toUpperCase()) {
            case 'PAID': return 'tw-bg-emerald-100 tw-text-emerald-700';
            case 'POSTED': return 'tw-bg-blue-100 tw-text-blue-700';
            case 'DRAFT': return 'tw-bg-gray-100 tw-text-gray-700';
            case 'PARTIAL': return 'tw-bg-amber-100 tw-text-amber-700';
            case 'VOID': return 'tw-bg-red-100 tw-text-red-700';
            case 'CANCELLED': return 'tw-bg-gray-200 tw-text-gray-600';
            default: return 'tw-bg-gray-100 tw-text-gray-700';
        }
    };

    const formatCurrency = (amount) => `Rs ${parseFloat(amount || 0).toLocaleString()}`;
    const formatPeriodName = (startDate, endDate, frequency) => {
        if (!startDate) return '';
        const start = new Date(startDate);
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                           'July', 'August', 'September', 'October', 'November', 'December'];
        const startMonth = monthNames[start.getMonth()];
        const startYear = start.getFullYear();
        if (frequency === -1 || !endDate) {
            return `${startMonth} ${startYear}`;
        }
        const end = new Date(endDate);
        const endMonth = monthNames[end.getMonth()];
        const endYear = end.getFullYear();
        if (startMonth === endMonth && startYear === endYear) {
            return `${startMonth} ${startYear}`;
        }
        return `${startMonth} ${startYear} - ${endMonth} ${endYear}`;
    };
    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return formatIstDate(dateStr);
    };

    const clearFilters = () => {
        setSearchQuery('');
        setFilterStatus('all');
        setFilterInvoiceType('all');
        setFilterWing('all');
        setFilterFlat('all');
        setFilterCarriedForward('all');
        setFromDate('');
        setToDate('');
        setSearchParams({ ...searchParams, page: 1 });
    };

    const hasActiveFilters = searchQuery || filterStatus !== 'all' || filterInvoiceType !== 'all' || filterWing !== 'all' || filterFlat !== 'all' || filterCarriedForward !== 'all' || fromDate || toDate;

    const handleViewInvoice = (invoice) => {
        setSelectedInvoice(invoice);
        dispatch(getInvoiceDetails({ id: invoice.invoice_id }));
        setShowDetailModal(true);
    };

    const closeModal = () => {
        setShowDetailModal(false);
        setSelectedInvoice(null);
    };

    const handleDelete = (invoice) => {
        if (window.confirm('Are you sure you want to delete this invoice?')) {
            dispatch(deleteInvoice({
                id: invoice.invoice_id,
                onSuccess: () => {
                    dispatch(getInvoices(searchParams));
                    dispatch(getInvoiceStatistics({ society_id: societyId }));
                }
            }));
        }
    };

    const handleVoid = (invoice) => {
        const reason = window.prompt('Enter reason for voiding this invoice:');
        if (reason) {
            dispatch(voidInvoice({
                id: invoice.invoice_id,
                reason,
                onSuccess: () => {
                    dispatch(getInvoices(searchParams));
                    dispatch(getInvoiceStatistics({ society_id: societyId }));
                    closeModal();
                }
            }));
        }
    };

    const handlePost = (invoice) => {
        if (window.confirm('Are you sure you want to post this invoice? This action cannot be undone.')) {
            dispatch(postInvoice({
                id: invoice.invoice_id,
                onSuccess: () => {
                    dispatch(getInvoices(searchParams));
                    dispatch(getInvoiceStatistics({ society_id: societyId }));
                    closeModal();
                }
            }));
        }
    };

    // Bulk selection helpers
    const toggleSelect = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const toggleSelectAll = () => {
        if (selectedIds.length === invoices.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(invoices.map(inv => inv.invoice_id));
        }
    };

    const clearSelection = () => setSelectedIds([]);

    // Bulk actions
    const handleBulkPost = () => {
        const draftIds = invoices.filter(inv => selectedIds.includes(inv.invoice_id) && inv.status === 'DRAFT').map(inv => inv.invoice_id);
        if (draftIds.length === 0) {
            alert('No draft invoices selected to post.');
            return;
        }
        if (!window.confirm(`Post ${draftIds.length} draft invoice(s)? This action cannot be undone.`)) return;
        dispatch(bulkPostInvoices({
            invoice_ids: draftIds,
            onSuccess: () => {
                dispatch(getInvoices(searchParams));
                dispatch(getInvoiceStatistics({ society_id: societyId }));
                setSelectedIds([]);
            }
        }));
    };

    const handleBulkDelete = () => {
        const deletableIds = invoices.filter(inv => selectedIds.includes(inv.invoice_id) && (inv.status === 'DRAFT' || inv.status === 'POSTED') && inv.is_carried_forward !== 1).map(inv => inv.invoice_id);
        if (deletableIds.length === 0) {
            alert('No deletable invoices selected.');
            return;
        }
        if (!window.confirm(`Delete ${deletableIds.length} invoice(s)? This cannot be undone.`)) return;
        dispatch(bulkDeleteInvoices({
            invoice_ids: deletableIds,
            onSuccess: () => {
                dispatch(getInvoices(searchParams));
                dispatch(getInvoiceStatistics({ society_id: societyId }));
                setSelectedIds([]);
            }
        }));
    };

    const handleBulkPrint = () => {
        if (selectedIds.length === 0) return;
        navigate(`${SOCIETY_PORTAL}${SOCIETY_PORTAL_INVOICES_BULK_PRINT}?ids=${selectedIds.join(',')}`);
    };

    const handlePrintInvoice = (invoice) => {
        navigate(`${SOCIETY_PORTAL}/finance/invoices/${invoice.invoice_id}/print`);
    };

    const handleEditInvoice = (invoice) => {
        navigate(`${SOCIETY_PORTAL}/finance/invoices/${invoice.invoice_id}/edit`);
    };

    const invoices = invoicesData || [];

    return (
        <>
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                <div>
                    <h1 className="tw-text-lg tw-font-semibold tw-text-gray-800">Invoices</h1>
                    <p className="tw-text-xs tw-text-gray-500">Manage society invoices</p>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                    <button
                        onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_INVOICES_BULK_CREATE)}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-bg-emerald-600 tw-text-white tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-emerald-700"
                    >
                        <Icon icon="mdi:file-document-multiple-outline" /> Bulk Create
                    </button>
                    <button
                        onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_INVOICES_CREATE)}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-bg-blue-600 tw-text-white tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-blue-700"
                    >
                        <Icon icon="mdi:plus" /> Create Invoice
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-6 tw-gap-4 tw-mb-6">
                {[
                    { icon: 'mdi:file-document-outline', color: 'tw-text-blue-600', bg: 'tw-bg-blue-50', value: stats.total, label: 'Total Invoices' },
                    { icon: 'mdi:check-circle', color: 'tw-text-emerald-600', bg: 'tw-bg-emerald-50', value: stats.paid, label: 'Paid' },
                    { icon: 'mdi:clock-outline', color: 'tw-text-amber-600', bg: 'tw-bg-amber-50', value: stats.pending, label: 'Pending' },
                    { icon: 'mdi:alert-circle', color: 'tw-text-red-600', bg: 'tw-bg-red-50', value: stats.overdue, label: 'Partial' },
                    { icon: 'mdi:cash-check', color: 'tw-text-teal-600', bg: 'tw-bg-teal-50', value: formatCurrency(stats.collectedAmount), label: 'Collected' },
                    { icon: 'mdi:cash-clock', color: 'tw-text-purple-600', bg: 'tw-bg-purple-50', value: formatCurrency(stats.pendingAmount), label: 'Outstanding' },
                ].map((stat, i) => (
                    <div key={i} className="tw-bg-white tw-rounded-xl tw-p-4 tw-shadow-sm tw-border tw-border-gray-200 tw-flex tw-flex-col tw-gap-3">
                        <div className="tw-flex tw-items-center tw-justify-between">
                            <div className={`tw-w-10 tw-h-10 tw-rounded-xl ${stat.bg} tw-flex tw-items-center tw-justify-center`}>
                                <Icon icon={stat.icon} className={`tw-text-xl ${stat.color}`} />
                            </div>
                        </div>
                        <div>
                            <p className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-leading-tight">
                                {statisticsLoading ? '...' : stat.value}
                            </p>
                            <p className="tw-text-xs tw-font-medium tw-text-gray-500 tw-mt-1">{stat.label}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Filter Bar */}
            <div className="tw-bg-white tw-rounded-xl tw-p-3 tw-mb-6 tw-shadow-sm tw-border tw-border-gray-200">
                <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
                    <div className="tw-flex-1 tw-min-w-[280px] tw-relative">
                        <Icon icon="mdi:magnify" className="tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-text-lg" />
                        <input
                            type="text"
                            placeholder="Search by invoice no, member or unit..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="tw-w-full tw-pl-10 tw-pr-4 tw-py-2 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 tw-transition-shadow"
                        />
                    </div>
                    <div className="tw-relative">
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="tw-w-40 tw-pl-3 tw-pr-8 tw-py-2 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-appearance-none focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 tw-transition-shadow"
                        >
                            <option value="all">All Status</option>
                            {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                        <Icon icon="mdi:chevron-down" className="tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-pointer-events-none" />
                    </div>
                    <button
                        onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                        className={`tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-lg tw-text-sm tw-font-medium tw-transition-colors ${showAdvancedFilters ? 'tw-bg-blue-100 tw-text-blue-700' : 'tw-bg-gray-50 tw-text-gray-600 hover:tw-bg-gray-100 tw-border tw-border-gray-200'}`}
                    >
                        <Icon icon="mdi:filter-variant" className="tw-text-base" /> Filters
                        {hasActiveFilters && <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-blue-600"></span>}
                    </button>
                    {hasActiveFilters && (
                        <button onClick={clearFilters} className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-red-600 tw-bg-red-50 tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-red-100 tw-transition-colors">
                            <Icon icon="mdi:close" className="tw-text-base" /> Clear
                        </button>
                    )}
                    <div className="tw-flex tw-bg-gray-100 tw-p-1 tw-rounded-lg tw-border tw-border-gray-200">
                        <button onClick={() => setViewMode('grid')} className={`tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-md tw-transition-colors ${viewMode === 'grid' ? 'tw-bg-white tw-text-blue-600 tw-shadow-sm' : 'tw-text-gray-500 hover:tw-text-gray-700'}`}>
                            <Icon icon="mdi:view-grid" className="tw-text-lg" />
                        </button>
                        <button onClick={() => setViewMode('list')} className={`tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-md tw-transition-colors ${viewMode === 'list' ? 'tw-bg-white tw-text-blue-600 tw-shadow-sm' : 'tw-text-gray-500 hover:tw-text-gray-700'}`}>
                            <Icon icon="mdi:view-list" className="tw-text-lg" />
                        </button>
                    </div>
                    <div className="tw-px-2 tw-border-l tw-border-gray-200">
                        <span className="tw-text-sm tw-font-medium tw-text-gray-600">
                            {invoices.length} <span className="tw-text-gray-400 tw-font-normal">of</span> {invoicesPagination?.total || invoices.length}
                        </span>
                    </div>
                </div>

                {/* Advanced Filters */}
                {showAdvancedFilters && (
                    <div className="tw-mt-3 tw-pt-3 tw-border-t tw-border-gray-200 tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-6 tw-gap-3">
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">Invoice Type</label>
                            <div className="tw-relative">
                                <select
                                    value={filterInvoiceType}
                                    onChange={(e) => { setFilterInvoiceType(e.target.value); setSearchParams({ ...searchParams, page: 1 }); }}
                                    className="tw-w-full tw-pl-3 tw-pr-8 tw-py-2 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-appearance-none focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 tw-transition-shadow"
                                >
                                    <option value="all">All Types</option>
                                    {(invoiceTypesData || []).map(t => <option key={t.invoice_type_id} value={t.invoice_type_id}>{t.invoice_title}</option>)}
                                </select>
                                <Icon icon="mdi:chevron-down" className="tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-pointer-events-none tw-text-sm" />
                            </div>
                        </div>
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">Wing</label>
                            <div className="tw-relative">
                                <select
                                    value={filterWing}
                                    onChange={(e) => { setFilterWing(e.target.value); setSearchParams({ ...searchParams, page: 1 }); }}
                                    className="tw-w-full tw-pl-3 tw-pr-8 tw-py-2 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-appearance-none focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 tw-transition-shadow"
                                >
                                    <option value="all">All Wings</option>
                                    {(wingsDropdown || []).map(w => <option key={w.wing_id} value={w.wing_id}>{w.title}</option>)}
                                </select>
                                <Icon icon="mdi:chevron-down" className="tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-pointer-events-none tw-text-sm" />
                            </div>
                        </div>
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">Flat</label>
                            <div className="tw-relative">
                                <select
                                    value={filterFlat}
                                    onChange={(e) => { setFilterFlat(e.target.value); setSearchParams({ ...searchParams, page: 1 }); }}
                                    disabled={filterWing === 'all'}
                                    className="tw-w-full tw-pl-3 tw-pr-8 tw-py-2 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-appearance-none focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 tw-transition-shadow disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                                >
                                    <option value="all">{filterWing === 'all' ? 'Select Wing First' : 'All Flats'}</option>
                                    {(flatsDropdown || []).map(f => <option key={f.flat_id} value={f.flat_id}>{f.flat_number}</option>)}
                                </select>
                                <Icon icon="mdi:chevron-down" className="tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-pointer-events-none tw-text-sm" />
                            </div>
                        </div>
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">Carried Forward</label>
                            <div className="tw-relative">
                                <select
                                    value={filterCarriedForward}
                                    onChange={(e) => { setFilterCarriedForward(e.target.value); setSearchParams({ ...searchParams, page: 1 }); }}
                                    className="tw-w-full tw-pl-3 tw-pr-8 tw-py-2 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-appearance-none focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 tw-transition-shadow"
                                >
                                    <option value="all">All</option>
                                    <option value="1">Yes</option>
                                    <option value="0">No</option>
                                </select>
                                <Icon icon="mdi:chevron-down" className="tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-pointer-events-none tw-text-sm" />
                            </div>
                        </div>
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">From Date</label>
                            <input
                                type="date"
                                value={fromDate}
                                onChange={(e) => { setFromDate(e.target.value); setSearchParams({ ...searchParams, page: 1 }); }}
                                className="tw-w-full tw-px-3 tw-py-2 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 tw-transition-shadow"
                            />
                        </div>
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">To Date</label>
                            <input
                                type="date"
                                value={toDate}
                                onChange={(e) => { setToDate(e.target.value); setSearchParams({ ...searchParams, page: 1 }); }}
                                className="tw-w-full tw-px-3 tw-py-2 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 tw-transition-shadow"
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Bulk Action Bar */}
            {selectedIds.length > 0 && (
                <div className="tw-bg-indigo-50 tw-border tw-border-indigo-200 tw-rounded-xl tw-px-4 tw-py-3 tw-mb-4 tw-flex tw-items-center tw-justify-between tw-animate-in">
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <div className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-indigo-600 tw-text-white tw-flex tw-items-center tw-justify-center tw-text-sm tw-font-bold">
                            {selectedIds.length}
                        </div>
                        <span className="tw-text-sm tw-font-medium tw-text-indigo-900">
                            invoice{selectedIds.length > 1 ? 's' : ''} selected
                        </span>
                        <button onClick={clearSelection} className="tw-text-xs tw-text-indigo-600 hover:tw-text-indigo-800 tw-font-medium tw-underline">
                            Clear
                        </button>
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <button
                            onClick={handleBulkPost}
                            disabled={bulkPostLoading}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-bg-emerald-600 tw-text-white tw-rounded-lg tw-text-xs tw-font-semibold hover:tw-bg-emerald-700 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                        >
                            <Icon icon={bulkPostLoading ? "mdi:loading" : "mdi:check-circle-outline"} className={`tw-text-sm ${bulkPostLoading ? 'tw-animate-spin' : ''}`} /> {bulkPostLoading ? 'Posting...' : 'Post'}
                        </button>
                        <button
                            onClick={handleBulkPrint}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-bg-indigo-600 tw-text-white tw-rounded-lg tw-text-xs tw-font-semibold hover:tw-bg-indigo-700 tw-transition-colors"
                        >
                            <Icon icon="mdi:printer" className="tw-text-sm" /> Print
                        </button>
                        <button
                            onClick={handleBulkDelete}
                            disabled={bulkDeleteLoading}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-bg-red-600 tw-text-white tw-rounded-lg tw-text-xs tw-font-semibold hover:tw-bg-red-700 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                        >
                            <Icon icon={bulkDeleteLoading ? "mdi:loading" : "mdi:trash-can-outline"} className={`tw-text-sm ${bulkDeleteLoading ? 'tw-animate-spin' : ''}`} /> {bulkDeleteLoading ? 'Deleting...' : 'Delete'}
                        </button>
                    </div>
                </div>
            )}

            {/* Loading State */}
            {loading && (
                <div className="tw-flex tw-justify-center tw-items-center tw-py-12">
                    <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-blue-600"></div>
                </div>
            )}

            {/* List View */}
            {!loading && viewMode === 'list' && (
                <div className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-200 tw-overflow-hidden">
                    <div className="tw-overflow-x-auto">
                        <table className="tw-w-full tw-text-sm tw-text-left">
                            <thead className="tw-bg-gray-50/80 tw-border-b tw-border-gray-200">
                                <tr>
                                    <th className="tw-px-3 tw-py-3.5 tw-w-10">
                                        <input
                                            type="checkbox"
                                            checked={invoices.length > 0 && selectedIds.length === invoices.length}
                                            onChange={toggleSelectAll}
                                            className="form-check-input"
                                        />
                                    </th>
                                    <th className="tw-px-4 tw-py-3.5 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Invoice No</th>
                                    <th className="tw-px-4 tw-py-3.5 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Unit</th>
                                    <th className="tw-px-4 tw-py-3.5 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Member</th>
                                    <th className="tw-px-4 tw-py-3.5 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Bill Date</th>
                                    <th className="tw-px-4 tw-py-3.5 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Period</th>
                                    <th className="tw-px-4 tw-py-3.5 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Due Date</th>
                                    <th className="tw-px-4 tw-py-3.5 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-text-right">Amount</th>
                                    <th className="tw-px-4 tw-py-3.5 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-text-right">Paid</th>
                                    <th className="tw-px-4 tw-py-3.5 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-text-right">Balance</th>
                                    <th className="tw-px-4 tw-py-3.5 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Status</th>
                                    <th className="tw-px-4 tw-py-3.5 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-text-center">Actions</th>
                                </tr>
                            </thead>
                        <tbody>
                            {invoices.map((invoice) => {
                                const isCarriedForward = invoice.is_carried_forward === 1;
                                const canEdit = !isCarriedForward && (invoice.status === 'DRAFT');
                                const canDelete = !isCarriedForward && (invoice.status === 'DRAFT' || invoice.status === 'POSTED');

                                return (
                                <tr key={invoice.invoice_id} className={`tw-border-b tw-border-gray-100 hover:tw-bg-gray-50/80 tw-transition-colors ${isCarriedForward ? 'tw-bg-orange-50/20' : ''} ${selectedIds.includes(invoice.invoice_id) ? 'tw-bg-indigo-50/40' : ''}`}>
                                    <td className="tw-px-3 tw-py-3">
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.includes(invoice.invoice_id)}
                                            onChange={() => toggleSelect(invoice.invoice_id)}
                                            className="form-check-input"
                                        />
                                    </td>
                                    <td className="tw-px-4 tw-py-3">
                                        <div className="tw-flex tw-items-center tw-gap-3">
                                            <div className={`tw-w-9 tw-h-9 tw-rounded-lg ${isCarriedForward ? 'tw-bg-orange-100 tw-text-orange-600' : 'tw-bg-blue-50 tw-text-blue-600'} tw-flex tw-items-center tw-justify-center tw-flex-shrink-0`}>
                                                <Icon icon={isCarriedForward ? "mdi:file-arrow-left-right-outline" : "mdi:file-document-outline"} className="tw-text-lg" />
                                            </div>
                                            <div>
                                                <span className="tw-font-medium tw-text-gray-900">{invoice.document_number || '-'}</span>
                                                <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5">{invoice.invoiceType?.invoice_title || 'Invoice'}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="tw-px-4 tw-py-3">
                                        <p className="tw-font-medium tw-text-gray-900">{invoice.flat?.wing?.title}-{invoice.flat?.flat_number || '-'}</p>
                                        <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5">Floor {invoice.flat?.floor_number || '-'}</p>
                                    </td>
                                    <td className="tw-px-4 tw-py-3">
                                        <p className="tw-font-medium tw-text-gray-900">{invoice.member_details?.name || '-'}</p>
                                        {invoice.member_details?.email && (
                                            <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5">{invoice.member_details.email}</p>
                                        )}
                                        {invoice.member_details?.gst_no && (
                                            <p className="tw-text-xs tw-text-blue-600 tw-mt-0.5">GST: {invoice.member_details.gst_no}</p>
                                        )}
                                    </td>
                                    <td className="tw-px-4 tw-py-3 tw-text-gray-600">{formatDate(invoice.bill_date)}</td>
                                    <td className="tw-px-4 tw-py-3 tw-text-gray-600 tw-text-xs">{formatPeriodName(invoice.period_start, invoice.period_end, invoice.frequency)}</td>
                                    <td className="tw-px-4 tw-py-3">
                                        <span className={`tw-inline-block tw-px-2 tw-py-1 tw-rounded-md tw-text-xs tw-font-medium ${new Date(invoice.due_date) < new Date() && invoice.status !== 'PAID' ? 'tw-bg-red-50 tw-text-red-700' : 'tw-bg-gray-50 tw-text-gray-700'}`}>
                                            {formatDate(invoice.due_date)}
                                        </span>
                                    </td>
                                    <td className="tw-px-4 tw-py-3 tw-text-right">
                                        <p className="tw-font-semibold tw-text-gray-900">{formatCurrency(invoice.net_payable)}</p>
                                        {parseFloat(invoice.tax_amount) > 0 && (
                                            <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5">Tax: {formatCurrency(invoice.tax_amount)}</p>
                                        )}
                                    </td>
                                    <td className="tw-px-4 tw-py-3 tw-text-right">
                                        <span className={`tw-font-medium ${parseFloat(invoice.amount_paid) > 0 ? 'tw-text-emerald-600' : 'tw-text-gray-400'}`}>
                                            {formatCurrency(invoice.amount_paid)}
                                        </span>
                                    </td>
                                    <td className="tw-px-4 tw-py-3 tw-text-right">
                                        <span className={`tw-font-semibold ${parseFloat(invoice.balance_due) > 0 ? 'tw-text-red-600' : 'tw-text-emerald-600'}`}>
                                            {formatCurrency(invoice.balance_due)}
                                        </span>
                                    </td>
                                    <td className="tw-px-4 tw-py-3">
                                        <div className="tw-flex tw-flex-col tw-gap-1.5 tw-w-fit">
                                            <span className={`tw-px-2.5 tw-py-1 tw-rounded-md tw-text-[11px] tw-font-medium tw-leading-none tw-inline-flex tw-items-center ${getStatusColor(invoice.status)}`}>
                                                {invoice.status}
                                            </span>
                                            {isCarriedForward && (
                                                <span className="tw-px-2.5 tw-py-1 tw-rounded-md tw-text-[11px] tw-font-medium tw-leading-none tw-bg-orange-100 tw-text-orange-700 tw-inline-flex tw-items-center">
                                                    C/F
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="tw-px-4 tw-py-3">
                                        <div className="tw-flex tw-items-center tw-justify-center tw-gap-1.5">
                                            <button onClick={() => handleViewInvoice(invoice)} className="tw-p-1.5 tw-rounded-md hover:tw-bg-blue-50 tw-text-gray-400 hover:tw-text-blue-600 tw-transition-colors" title="View Details">
                                                <Icon icon="mdi:eye-outline" className="tw-text-lg" />
                                            </button>
                                            {canEdit && (
                                                <button
                                                    onClick={() => handleEditInvoice(invoice)}
                                                    className="tw-p-1.5 tw-rounded-md hover:tw-bg-indigo-50 tw-text-gray-400 hover:tw-text-indigo-600 tw-transition-colors"
                                                    title="Edit Draft"
                                                >
                                                    <Icon icon="mdi:pencil-outline" className="tw-text-lg" />
                                                </button>
                                            )}
                                            {canEdit && (
                                                <button
                                                    onClick={() => handlePost(invoice)}
                                                    className="tw-p-1.5 tw-rounded-md hover:tw-bg-green-50 tw-text-gray-400 hover:tw-text-green-600 tw-transition-colors"
                                                    title="Post Invoice"
                                                    disabled={postLoading}
                                                >
                                                    <Icon icon="mdi:check-circle-outline" className="tw-text-lg" />
                                                </button>
                                            )}
                                            <button onClick={() => handlePrintInvoice(invoice)} className="tw-p-1.5 tw-rounded-md hover:tw-bg-purple-50 tw-text-gray-400 hover:tw-text-purple-600 tw-transition-colors" title="Print Invoice">
                                                <Icon icon="mdi:printer" className="tw-text-lg" />
                                            </button>
                                            {canDelete && (
                                                <button
                                                    onClick={() => handleDelete(invoice)}
                                                    className="tw-p-1.5 tw-rounded-md hover:tw-bg-red-50 tw-text-gray-400 hover:tw-text-red-600 tw-transition-colors"
                                                    title="Delete"
                                                    disabled={deleteLoading}
                                                >
                                                    <Icon icon="mdi:trash-can-outline" className="tw-text-lg" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    </div>
                    {invoices.length === 0 && (
                        <div className="tw-text-center tw-py-12 tw-bg-gray-50/50">
                            <div className="tw-inline-flex tw-items-center tw-justify-center tw-w-16 tw-h-16 tw-rounded-full tw-bg-gray-100 tw-mb-4">
                                <Icon icon="mdi:file-document-outline" className="tw-text-3xl tw-text-gray-400" />
                            </div>
                            <h3 className="tw-font-medium tw-text-gray-900 tw-mb-1">No Invoices Found</h3>
                            <p className="tw-text-gray-500 tw-text-sm">Try adjusting your filters or search query.</p>
                        </div>
                    )}
                </div>
            )}

            {/* Grid View */}
            {!loading && viewMode === 'grid' && (
                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-4">
                    {invoices.map((invoice) => {
                        const isCarriedForward = invoice.is_carried_forward === 1;
                        const canEdit = !isCarriedForward && (invoice.status === 'DRAFT');
                        const canDelete = !isCarriedForward && (invoice.status === 'DRAFT' || invoice.status === 'POSTED');

                        return (
                        <div key={invoice.invoice_id} className={`tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-p-4 tw-transition-shadow hover:tw-shadow-md ${isCarriedForward ? 'tw-border-orange-200 tw-bg-orange-50/20' : 'tw-border-gray-200'} ${selectedIds.includes(invoice.invoice_id) ? 'tw-ring-2 tw-ring-indigo-400' : ''}`}>
                            <div className="tw-flex tw-items-start tw-justify-between tw-mb-4">
                                <div className="tw-flex tw-items-center tw-gap-3">
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.includes(invoice.invoice_id)}
                                        onChange={() => toggleSelect(invoice.invoice_id)}
                                        className="tw-w-4 tw-h-4 tw-rounded tw-border-gray-300 tw-text-indigo-600 focus:tw-ring-indigo-500 tw-flex-shrink-0"
                                    />
                                    <div className={`tw-w-11 tw-h-11 tw-rounded-lg ${isCarriedForward ? 'tw-bg-orange-100 tw-text-orange-600' : 'tw-bg-blue-50 tw-text-blue-600'} tw-flex tw-items-center tw-justify-center tw-flex-shrink-0`}>
                                        <Icon icon={isCarriedForward ? "mdi:file-arrow-left-right-outline" : "mdi:file-document-outline"} className="tw-text-xl" />
                                    </div>
                                    <div>
                                        <p className="tw-font-bold tw-text-gray-900">{invoice.document_number || '-'}</p>
                                        <p className="tw-text-xs tw-text-gray-500 tw-font-medium">{invoice.invoiceType?.invoice_title || 'Invoice'}</p>
                                    </div>
                                </div>
                                <div className="tw-flex tw-flex-col tw-items-end tw-gap-1.5">
                                    <span className={`tw-px-2.5 tw-py-1 tw-rounded-md tw-text-[11px] tw-font-medium tw-leading-none ${getStatusColor(invoice.status)}`}>
                                        {invoice.status}
                                    </span>
                                    {isCarriedForward && (
                                        <span className="tw-px-2.5 tw-py-1 tw-rounded-md tw-text-[11px] tw-font-medium tw-leading-none tw-bg-orange-100 tw-text-orange-700">
                                            C/F
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="tw-space-y-2.5 tw-py-3 tw-border-t tw-border-b tw-border-gray-100">
                                <div className="tw-flex tw-items-center tw-justify-between tw-text-sm">
                                    <span className="tw-text-gray-500 tw-flex tw-items-center tw-gap-1.5"><Icon icon="mdi:home-outline" className="tw-text-gray-400 tw-text-base" /> Unit:</span>
                                    <span className="tw-font-medium tw-text-gray-900">{invoice.flat?.wing?.title}-{invoice.flat?.flat_number || '-'}</span>
                                </div>
                                <div className="tw-flex tw-items-start tw-justify-between tw-text-sm">
                                    <span className="tw-text-gray-500 tw-flex tw-items-center tw-gap-1.5"><Icon icon="mdi:account-outline" className="tw-text-gray-400 tw-text-base" /> Member:</span>
                                    <div className="tw-text-right">
                                        <span className="tw-font-medium tw-text-gray-900">{invoice.member_details?.name || '-'}</span>
                                        {invoice.member_details?.email && (
                                            <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5">{invoice.member_details.email}</p>
                                        )}
                                        {invoice.member_details?.gst_no && (
                                            <p className="tw-text-xs tw-blue-600 tw-mt-0.5">GST: {invoice.member_details.gst_no}</p>
                                        )}
                                    </div>
                                </div>
                                <div className="tw-flex tw-items-center tw-justify-between tw-text-sm">
                                    <span className="tw-text-gray-500 tw-flex tw-items-center tw-gap-1.5"><Icon icon="mdi:calendar-outline" className="tw-text-gray-400 tw-text-base" /> Bill Date:</span>
                                    <span className="tw-text-gray-900">{formatDate(invoice.bill_date)}</span>
                                </div>
                                <div className="tw-flex tw-items-center tw-justify-between tw-text-sm">
                                    <span className="tw-text-gray-500 tw-flex tw-items-center tw-gap-1.5"><Icon icon="mdi:calendar-range" className="tw-text-gray-400 tw-text-base" /> Period:</span>
                                    <span className="tw-text-gray-900">{formatPeriodName(invoice.period_start, invoice.period_end, invoice.frequency)}</span>
                                </div>
                                <div className="tw-flex tw-items-center tw-justify-between tw-text-sm">
                                    <span className="tw-text-gray-500 tw-flex tw-items-center tw-gap-1.5"><Icon icon="mdi:calendar-clock" className="tw-text-gray-400 tw-text-base" /> Due:</span>
                                    <span className={`tw-font-medium ${new Date(invoice.due_date) < new Date() && invoice.status !== 'PAID' ? 'tw-text-red-600' : 'tw-text-gray-900'}`}>{formatDate(invoice.due_date)}</span>
                                </div>
                            </div>

                            <div className="tw-py-3 tw-space-y-2">
                                <div className="tw-flex tw-items-center tw-justify-between">
                                    <span className="tw-text-sm tw-text-gray-500">Amount:</span>
                                    <span className="tw-font-bold tw-text-gray-900 tw-text-base">{formatCurrency(invoice.net_payable)}</span>
                                </div>
                                {parseFloat(invoice.tax_amount) > 0 && (
                                    <div className="tw-flex tw-items-center tw-justify-between">
                                        <span className="tw-text-xs tw-text-gray-400">Tax:</span>
                                        <span className="tw-text-xs tw-text-gray-500">{formatCurrency(invoice.tax_amount)}</span>
                                    </div>
                                )}
                                <div className="tw-flex tw-items-center tw-justify-between">
                                    <span className="tw-text-sm tw-text-gray-500">Paid:</span>
                                    <span className={`tw-text-sm tw-font-semibold ${parseFloat(invoice.amount_paid) > 0 ? 'tw-text-emerald-600' : 'tw-text-gray-400'}`}>
                                        {formatCurrency(invoice.amount_paid)}
                                    </span>
                                </div>
                                <div className="tw-flex tw-items-center tw-justify-between tw-pt-2 tw-mt-1 tw-border-t tw-border-dashed tw-border-gray-200">
                                    <span className="tw-text-sm tw-font-semibold tw-text-gray-700">Balance:</span>
                                    <span className={`tw-text-lg tw-font-black ${parseFloat(invoice.balance_due) > 0 ? 'tw-text-red-600' : 'tw-text-emerald-600'}`}>
                                        {formatCurrency(invoice.balance_due)}
                                    </span>
                                </div>
                            </div>

                            <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-pt-3 tw-border-t tw-border-gray-100">
                                <button onClick={() => handleViewInvoice(invoice)} className="tw-p-1.5 tw-rounded-md hover:tw-bg-blue-50 tw-text-gray-400 hover:tw-text-blue-600 tw-transition-colors" title="View Details">
                                    <Icon icon="mdi:eye-outline" className="tw-text-lg" />
                                </button>
                                {canEdit && (
                                    <button
                                        onClick={() => handleEditInvoice(invoice)}
                                        className="tw-p-1.5 tw-rounded-md hover:tw-bg-indigo-50 tw-text-gray-400 hover:tw-text-indigo-600 tw-transition-colors"
                                        title="Edit Draft"
                                    >
                                        <Icon icon="mdi:pencil-outline" className="tw-text-lg" />
                                    </button>
                                )}
                                {canEdit && (
                                    <button
                                        onClick={() => handlePost(invoice)}
                                        className="tw-p-1.5 tw-rounded-md hover:tw-bg-green-50 tw-text-gray-400 hover:tw-text-green-600 tw-transition-colors"
                                        title="Post Invoice"
                                        disabled={postLoading}
                                    >
                                        <Icon icon="mdi:check-circle-outline" className="tw-text-lg" />
                                    </button>
                                )}
                                <button onClick={() => handlePrintInvoice(invoice)} className="tw-p-1.5 tw-rounded-md hover:tw-bg-purple-50 tw-text-gray-400 hover:tw-text-purple-600 tw-transition-colors" title="Print Invoice">
                                    <Icon icon="mdi:printer" className="tw-text-lg" />
                                </button>
                                {canDelete && (
                                    <button
                                        onClick={() => handleDelete(invoice)}
                                        className="tw-p-1.5 tw-rounded-md hover:tw-bg-red-50 tw-text-gray-400 hover:tw-text-red-600 tw-transition-colors"
                                        title="Delete"
                                        disabled={deleteLoading}
                                    >
                                        <Icon icon="mdi:trash-can-outline" className="tw-text-lg" />
                                    </button>
                                )}
                            </div>
                        </div>
                        );
                    })}
                </div>
            )}

            {/* Pagination */}
            {!loading && invoicesPagination && invoicesPagination.total > 0 && (
                <PaginationLimit
                    currentPage={invoicesPagination.page || searchParams.page}
                    totalRecords={invoicesPagination.total || 0}
                    limit={searchParams.limit}
                    handleLimitChange={(e) => setSearchParams({ ...searchParams, limit: parseInt(e.target.value), page: 1 })}
                    handlePageChange={(page) => setSearchParams({ ...searchParams, page })}
                />
            )}

            {/* Invoice Detail Modal */}
            {showDetailModal && selectedInvoice && (
                <InvoiceDetailModal
                    invoice={invoiceDetails || selectedInvoice}
                    loading={detailsLoading}
                    onClose={closeModal}
                    onVoid={() => handleVoid(selectedInvoice)}
                    onPost={() => handlePost(selectedInvoice)}
                    voidLoading={voidLoading}
                    postLoading={postLoading}
                />
            )}
        </>
    );
};

export default Invoices;
